import { useState } from 'react';

import { Drawer } from '../../../../components/Drawer';
import Dropdown from '../../../../components/Dropdown';
import { HelpDrawerLink } from '../../../../components/HelpDrawerLink';
import TextField from '../../../../components/TextField';
import { WorkflowSubStepProps } from '../../config';

export interface SelectiveServiceFormData {
  bornMaleAfter1959: boolean | null;
  registeredWithSelectiveService: boolean | null;
  selectiveServiceExplanation: string;
}

const yesNoOptions = [
  { label: '- Select an option -', value: null },
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

function SelectiveServicePage({ data, updateData, fieldErrors }: WorkflowSubStepProps<SelectiveServiceFormData>) {
  const [isHelpDrawerOpen, setIsHelpDrawerOpen] = useState(false);
  const baseTestId = 'selective-service-page';

  const handleBornMaleChange = (value: boolean | null) => {
    // If changing from Yes to No or clearing, reset subsequent fields
    if (value !== true) {
      updateData({
        ...data,
        bornMaleAfter1959: value,
        registeredWithSelectiveService: null,
        selectiveServiceExplanation: '',
      });
    } else {
      updateData({
        ...data,
        bornMaleAfter1959: value,
      });
    }
  };

  const handleRegisteredChange = (value: boolean | null) => {
    // If changing to Yes, clear the explanation field
    if (value === true) {
      updateData({
        ...data,
        registeredWithSelectiveService: value,
        selectiveServiceExplanation: '',
      });
    } else {
      updateData({
        ...data,
        registeredWithSelectiveService: value,
      });
    }
  };

  const handleExplanationChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    updateData({
      ...data,
      selectiveServiceExplanation: event.target.value,
    });
  };

  const showSelectiveServiceQuestion = data.bornMaleAfter1959 === true;
  const showExplanationField = showSelectiveServiceQuestion && data.registeredWithSelectiveService === false;

  return (
    <div data-testid={baseTestId}>
      <div
        className='ds-u-margin-bottom--3'
        data-testid={`${baseTestId}-born-male`}>
        <Dropdown
          required
          data-testid={`${baseTestId}-born-male-dropdown`}
          errorMessage={fieldErrors?.bornMaleAfter1959}
          label='Were you born a male after December 31, 1959?'
          name='bornMaleAfter1959'
          options={yesNoOptions}
          tooltipText='This question is required for Selective Service System registration compliance.'
          value={data.bornMaleAfter1959}
          onChange={handleBornMaleChange}
        />
      </div>

      {showSelectiveServiceQuestion && (
        <div
          className='ds-u-margin-bottom--3 nested-form-field'
          data-testid={`${baseTestId}-registered`}>
          <Dropdown
            required
            data-testid={`${baseTestId}-registered-dropdown`}
            errorMessage={fieldErrors?.registeredWithSelectiveService}
            label='Have you registered with the Selective Service System?'
            name='registeredWithSelectiveService'
            options={yesNoOptions}
            tooltipText='Male citizens and immigrants, ages 18 through 25, are required to register with Selective Service.'
            value={data.registeredWithSelectiveService}
            onChange={handleRegisteredChange}
          />
        </div>
      )}

      {showExplanationField && (
        <div
          className='ds-u-margin-bottom--3 nested-form-field'
          data-testid={`${baseTestId}-explanation`}>
          <TextField
            multiline
            required
            data-testid={`${baseTestId}-explanation-field`}
            defaultValue={data.selectiveServiceExplanation || ''}
            errorMessage={fieldErrors?.selectiveServiceExplanation}
            label='If "No", explain why you have not registered with the Selective Service System.'
            name='selectiveServiceExplanation'
            rows={5}
            onChange={handleExplanationChange}
          />
        </div>
      )}

      <div
        className='ds-u-border-top--2 ds-u-padding-top--3 ds-u-margin-top--4 field-max-width'
        data-testid={`${baseTestId}-help`}>
        <HelpDrawerLink
          data-testid={`${baseTestId}-help-link`}
          onClick={() => setIsHelpDrawerOpen(true)}
        />
      </div>

      <Drawer
        data-testid={`${baseTestId}-drawer`}
        heading='Help: Declaration of Federal Employment (OF-306) Questions'
        isOpen={isHelpDrawerOpen}
        onClose={() => setIsHelpDrawerOpen(false)}>
        <div className='ds-u-padding--3'>
          <h3 className='ds-u-margin-top--0'>Get Help with Selective Service Registration</h3>
          <p>
            The Military Selective Service Act says that almost all male U.S. citizens and male immigrants between 18
            and 25 have to sign up with the Selective Service System.
          </p>

          <h4 className='ds-u-margin-top--3'>Were you born a male after December 31, 1959?</h4>
          <p>
            If you were born male after December 31, 1959, you need to register with the Selective Service System. Some
            people may be exempt from this requirement, such as those on active duty in the Armed Forces.
          </p>

          <h4 className='ds-u-margin-top--3'>Have you registered with the Selective Service System?</h4>
          <p>
            If you have not registered with the Selective Service, you must explain why. If you have not registered,
            this may affect whether you are allowed to work for the government.
          </p>

          <p className='ds-u-margin-top--3'>
            <strong>Note:</strong> You can check your registration status or register online at{' '}
            <a
              aria-label='www.sss.gov (opens in a new window)'
              href='https://www.sss.gov/'
              rel='noopener noreferrer'
              target='_blank'>
              www.sss.gov
              <span className='sr-only'> (opens in a new window)</span>
            </a>
            .
          </p>
        </div>
      </Drawer>
    </div>
  );
}

export default SelectiveServicePage;
